import {
  toTitleInfo,
  toDescriptionInfo,
  toImagesInfo,
  toOutOfStock,
} from './newMenuPage.util';

import type {
  FullNewMenuCatalogRawResp,
  NewCategoryRawResp,
  NewItemRawResp,
} from './newMenuPage.response';
import type {
  FullMenuCatalog,
  IMenuCategory,
  IMenuCustomizationGroup,
  IMenuProduct,
} from 'features/menu-page/domain';

function toFullMenu(newMenu: FullNewMenuCatalogRawResp): FullMenuCatalog {
  const globalStoreId = newMenu.store_id;
  const menuDefault =
    newMenu.menus.find(_m => _m.menu_type === 'DEFAULT') ??
    ({
      id: null,
      store_id: NaN,
      menu_type: 'DEFAULT',
      super_store_id: NaN,
      categories: [],
    } as FullNewMenuCatalogRawResp['menus'][0]);

  const categoriesPrev = menuDefault.categories
    .filter(c => Boolean(c.id))
    .map(c => ({
      ...c,
      ...(newMenu.categories.find(_c => _c.id === c.id) as NewCategoryRawResp),
      store_id: globalStoreId,
    }));

  type ProductsPrev = NewItemRawResp & {
    index: number;
    category_id: number;
  };
  const productsPrev = categoriesPrev.reduce(
    (acc, c) => [
      ...acc,
      ...c.items
        .map(
          p =>
            ({
              index: p.index,
              category_id: c.id ?? NaN,
              ...(newMenu.items.find(item => item.id === p.id) ?? {}),
              store_id: globalStoreId,
            } as ProductsPrev),
        )
        .filter(p => Boolean(p.id) && p.item_type === 'ITEM'),
    ],
    [] as ProductsPrev[],
  );

  type CustomGroupsPrev = NewItemRawResp & {
    index: number;
    category_id: number;
    product_id: number;
  };
  const customGroupsPrev = Array.from(
    new Map(
      productsPrev
        .reduce(
          (acc, _pp) => [
            ...acc,
            ..._pp.items_info.items
              .filter(cg => newMenu.items.some(_item => _item.id === cg.id))
              .map(
                cg =>
                  ({
                    ...cg,
                    category_id: _pp.category_id,
                    product_id: _pp.id,
                    ...newMenu.items.find(_item => _item.id === cg.id),
                  } as CustomGroupsPrev),
              ),
          ],
          [] as CustomGroupsPrev[],
        )
        .filter(cg => Boolean(cg.id))
        .map(cg => [`${cg.id}+${cg.product_id}`, cg]),
    ).values(),
  );

  return {
    categories: categoriesPrev
      .map<IMenuCategory>(c => ({
        id: c.id ?? NaN,
        storeId: c.store_id,
        name: c.title,
        description: c.title,
        index: c.index,
        isOn: c.is_category_active,
      }))
      .toSorted((a, b) => a.index - b.index),

    products: productsPrev.map<IMenuProduct>(p => ({
      id: p.id ?? NaN,
      storeId: p.store_id,
      categoryId: p.category_id,
      index: p.index,
      name: toTitleInfo({
        storeId: globalStoreId,
        menuId: menuDefault.id ?? NaN,
        categoryId: p.category_id ?? NaN,
        itemId: p.id ?? NaN,
        titleInfo: p.title_info,
      }),
      description: toDescriptionInfo({
        storeId: globalStoreId,
        menuId: menuDefault.id ?? NaN,
        categoryId: p.category_id ?? NaN,
        itemId: p.id ?? NaN,
        descriptionInfo: p.description_info,
      }),
      image: toImagesInfo({
        storeId: globalStoreId,
        menuId: menuDefault.id ?? NaN,
        categoryId: p.category_id ?? NaN,
        itemId: p.id ?? NaN,
        imagesInfo: p.images_info,
      }),
      hasCustomizations: p.items_info.items.length > 0,
      ...toOutOfStock({
        storeId: globalStoreId,
        menuId: menuDefault.id ?? NaN,
        categoryId: p.category_id ?? NaN,
        itemId: NaN,
        suspensionInfo: p.suspension_info,
      }),
    })),

    customizationGroups: customGroupsPrev.map<IMenuCustomizationGroup>(cg => ({
      id: cg.id ?? NaN,
      index: cg.index,
      productId: cg.product_id,
      description: toTitleInfo({
        storeId: globalStoreId,
        menuId: menuDefault.id ?? NaN,
        categoryId: cg.category_id,
        itemId: cg.id ?? NaN,
        titleInfo: cg.title_info,
      }),
      items: cg.items_info.items.map(custom => ({
        id: custom.id ?? NaN,
        index: custom.index,
        ...(newMenu.items
          .filter(_item => _item.id === custom.id)
          .map(_item => ({
            description: toTitleInfo({
              storeId: globalStoreId,
              menuId: menuDefault.id ?? NaN,
              categoryId: cg.category_id,
              itemId: custom.id ?? NaN,
              titleInfo: _item.title_info,
            }),
            ...toOutOfStock({
              storeId: globalStoreId,
              menuId: menuDefault.id ?? NaN,
              categoryId: cg.category_id,
              itemId: cg.id ?? NaN,
              suspensionInfo: _item.suspension_info,
            }),
          }))[0] ?? {}),
      })),
    })),
  };
}

export const newMenuPageAdapter = {
  toFullMenu,
};
